import React, { useState, useEffect, useRef } from "react";
import "./tableStyle.css";
import { reportsApi,exportDoStatusExcel } from "../../Services/Services";
import { Col, Input, Button, Radio, Form, Select, message } from "antd";
import { useReactToPrint } from "react-to-print";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "../util.js";

export default function DoStatus() {
  const [deliveryOrder, setDeliveryOrder] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchPartyName, setSearchPartyName] = useState("");
  const [searchDoNo, setSearchDoNo] = useState("");
  const [liftingStatus, setLiftingStatus] = useState(""); 

  const [form] = Form.useForm();

  /* ---------------- utilities ---------------- */
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2022 }, (_, i) => 2023 + i);
  const componentRef = useRef();

  /* ----------- initial fetch ----------- */
  useEffect(() => {
    fetchData(
      0,
      selectedYear,
      searchPartyName,
      searchDoNo,
      liftingStatus
    );
  }, []);

  /* --------------- API call --------------- */
  const fetchData = async (
    offsetParam = 0,
    yearParam = selectedYear,
    partyParam = searchPartyName,
    doNoParam = searchDoNo,
    liftStatusParam = liftingStatus
  ) => {
    const params = {
      report_name: "do_status",
      com_name: localStorage.getItem("com_name"),
      lifter_id: localStorage.getItem("user_type") === "Lifter" ? localStorage.getItem("user_id") : "",
      year: yearParam,
      party_name: partyParam,
      do_no: doNoParam,
      lifting_status: liftStatusParam, 
      limit,
      offset: offsetParam,
    };

    const response = await reportsApi(params);
    if (response && response.results) {
      if (offsetParam === 0) setDeliveryOrder(response.results);
      else setDeliveryOrder((prev) => [...prev, ...response.results]);
      setHasMore(response.results.length >= limit);
    } else setHasMore(false);
  };

  /* --------------- Search with debounce --------------- */
const debouncedPartySearch = useRef(
  debounce((v) => {
    setSearchPartyName(v);
    setOffset(0);
    fetchData(0, selectedYear, v, searchDoNo, liftingStatus); 
  }, 800)
).current;

  const debouncedDoSearch = useRef(
    debounce((v) => {
      setSearchDoNo(v);
      setOffset(0);
      fetchData(
        0,
        selectedYear,
        searchPartyName,
        v,
        liftingStatus
      );
    }, 800)
  ).current;

  /* --------------- Handlers --------------- */
  const handleYearChange = (y) => {
    setSelectedYear(y);
    setOffset(0);
    fetchData(0, y, searchPartyName, searchDoNo, liftingStatus);
  };


  const handlePartySearch = (e) => debouncedPartySearch(e.target.value);
  const handleDoSearch = (e) => debouncedDoSearch(e.target.value);

  const handleLiftingStatusChange = (e) => {
    const val = e.target.value; // "", "pending", or "complete"
    setLiftingStatus(val);
    setOffset(0);
    fetchData(0, selectedYear, searchPartyName, searchDoNo, val);
  };

  const fetchMoreData = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchData(
      newOffset,
      selectedYear,
      searchPartyName,
      searchDoNo,
      liftingStatus
    );
  };

  /* --------------- Print / Excel --------------- */
  const handlePrint = useReactToPrint({ content: () => componentRef.current });

  // Add this function to your DoStatus component
const handleExportToExcel = async () => {
  try {
    // Show loading state (optional)
    const loadingMessage = message.loading("Generating Excel file...", 0);
    
    // Prepare parameters
    const exportParams = {
      com_name: localStorage.getItem("com_name"),
      year: selectedYear,
      party_name: searchPartyName,
      do_no: searchDoNo,
      lifting_status: liftingStatus,
      user_type: localStorage.getItem("user_type"),
      lifter_id: localStorage.getItem("user_type") === "Lifter" ? localStorage.getItem("user_id") : "", // will be used only if user_type is "Lifter"
    };
    
    // Call the export function
    const result = await exportDoStatusExcel(exportParams);
    
    if (result.success) {
      // Create blob URL and trigger download
      const blob = new Blob([result.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      
      // Extract filename from response headers or use default
      const contentDisposition = result.headers["content-disposition"];
      let filename = `DO_Status_${selectedYear}_${new Date().toISOString().split("T")[0]}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }
      
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      
      // Hide loading and show success message (optional)
      loadingMessage();
      message.success("Excel file downloaded successfully!");
    } else {
      // Hide loading and show error message
      loadingMessage();
      message.error(result.error || "Failed to export Excel file");
    }
  } catch (error) {
    console.error("Error exporting Excel:", error);
    message.error("An error occurred while exporting the file");
  }
};

  const formatDate = (d) => {
    const [y, m, day] = d.split("-");
    return `${day}-${m}-${y}`;
  };

  /* --------------- JSX --------------- */
  return (
    <div className="do-status-page">
      <Form form={form} layout="vertical">
        <div
          className="grid sm:grid-cols-5 gap-3 px-5 sm:w-[70%] md:w-[100%] m-auto"
          style={{ marginBottom: 10 }}
        >
          <Col>
            <label>
              <b>Select Year</b>
            </label>
            <br />
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              style={{ width: "95%" }}
            >
              {years.map((y) => (
                <Select.Option key={y} value={y}>
                  {y}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col>
            <label>
              <b>Search By Do No.</b>
            </label>
            <br />
            <Input placeholder="Search DO No" onChange={handleDoSearch} />
          </Col>
      {localStorage.getItem("user_type") !== "Lifter" && 
          <Col>
            <label>
              <b>Search By Party Name</b>
            </label>
            <br />
            <Input placeholder="Search Party" onChange={handlePartySearch} />
          </Col>
}
          <Col>
            <label>
              <b>Lifting Status</b>
            </label>
            <br />
            <Radio.Group
              onChange={handleLiftingStatusChange}
              value={liftingStatus}
            >
              <Radio value="pending">Pending</Radio>
              <Radio value="complete">Complete</Radio>
              <Radio value="">All</Radio>
            </Radio.Group>
          </Col>

          <Col>
            <Button
              style={{
                margin: 10,
                background: "black",
                color: "white",
                fontWeight: 600,
              }}
              onClick={handlePrint}
            >
              <FaFilePdf />
            </Button>
            <Button
              style={{
                margin: 10,
                background: "green",
                color: "white",
                fontWeight: 600,
              }}
              onClick={handleExportToExcel}
            >
              <FaFileExcel />
            </Button>
          </Col>
        </div>
      </Form>

      {/* TABLE */}
      <div
        id="scrollableDiv"
        ref={componentRef}
        style={{ maxHeight: 500, overflow: "auto" }}
      >
        <table className="w-full" id="my-table">
          <thead className="bg-[#151B54] text-white">
            <tr>
              <th>Sr. No</th>
              <th>Id</th>
              {localStorage.getItem("user_type") !== "Lifter" && 
              <th>Party Name</th>
              }
              <th>Do No.</th>
              <th>Mines</th>
              <th>DO Start</th>
              <th>DO End</th>
              <th>Total Qty</th>
              <th>Dispatch Qty</th>
              <th>Balance Qty</th>
              <th>Debit Amt</th>
              <th>Credit Amt</th>
              <th>Balance Amt</th>
              <th>Total Freight</th>
              <th>Status</th>
              <th>Leps / Sale</th>
              <th>Bill</th>
            </tr>
          </thead>
          <tbody>
            {deliveryOrder.map((e, i) => (
              <tr key={e.id}>
                <td>{i + 1}</td>
                <td>{e.id}</td>
                  {localStorage.getItem("user_type") !== "Lifter" && 
                <td>{e.client_name}</td>
                  }
                <td>
                  <Link to={`/do-view/${e.id}`}>
                    <u style={{ fontWeight: "600", color: "blue" }}>
                       {e.do_no}
                    </u>
                  </Link>
                </td>
                <td>{e.mines_name}</td>
                <td>{formatDate(e.do_start_date)}</td>
                <td>{formatDate(e.do_end_date)}</td>
                <td>{e.quantity}</td>
                <td>{e.dis_qty.toFixed(3)}</td>
                <td>{e.rem_qty.toFixed(3)}</td>
                <td>{e.total_advance_paid}</td>
                <td>{e.advance_amt}</td>
                <td>
                  {parseFloat(e.total_advance_paid) - parseFloat(e.advance_amt)}
                </td>
                <td>{e.paid_amt}</td>
                <td>{e.rem_qty !== 0 ? "Pending" : "Complete"}</td>
                <td>
                  {e.is_sell
                    ? `Sale-${e.sell_qty}`
                    : e.is_leps
                    ? `Leps-${e.leps_qty}`
                    : ""}
                </td>
                <td>{e.is_bill ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <InfiniteScroll
          dataLength={deliveryOrder.length}
          next={fetchMoreData}
          hasMore={hasMore}
          scrollableTarget="scrollableDiv"
          loader={
            deliveryOrder.length > 0 && hasMore ? (
              <div style={{ textAlign: "center", padding: 10 }}>Loading...</div>
            ) : null
          }
        />
      </div>
    </div>
  );
}
